export const SUCCESS_STATUS = 'succeed';
export const FAILURE_STATUS = 'failed';
export class SegmentService {
    static $inject = ['CurrentApplicationService', 'ContextProcessorService', 'AuthenticationService', 'AccountService2', 'BlipService', 'ChannelsService'];
    constructor(CurrentApplicationService, ContextProcessorService, AuthenticationService, AccountService2, BlipService, ChannelsService) {
        this.CurrentApplicationService = CurrentApplicationService;
        this.ContextProcessorService = ContextProcessorService;
        this.AuthenticationService = AuthenticationService;
        this.AccountService2 = AccountService2;
        this.BlipService = BlipService;
        this.ChannelsService = ChannelsService;
    }
    /**
     * Set account and identify user on Segment
     * @param additionalInfos
     * @param plan
     * @param subscription
     */
    async setAccount(additionalInfos, plan, subscription) {
        try {
            /**
             * Remove some sensitive account properties
             * @param account
             */
            const sanitizeInfos = (account) => {
                if (account) {
                    const { password, ...sanitizedAccount } = account;
                    return sanitizedAccount;
                }
                return {};
            };
            this.userAccount = await this.retrieveUser();
            const { email, fullName, phoneNumber, company, culture, created, userProfile, extras, } = this.userAccount;
            if (!email) {
                return;
            }
            analytics.identify(email, {
                email,
                name: fullName,
                phoneNumber,
                company,
                culture,
                created,
                version: '2.0',
                userProfile,
                subscription,
                plan,
                companyWebsite: extras ? extras.companyWebsite : undefined,
                employeesNumber: extras ? extras.employeesNumber : undefined,
                ...sanitizeInfos(additionalInfos),
            });
        }
        catch (e) {
            console.error(e);
        }
    }
    resetAccount() {
        this.userAccount = undefined;
        analytics.reset();
    }
    async createTrack(trackEvent, payload = {}) {
        try {
            if (!trackEvent) {
                return;
            }
            const defaultTrackProps = this.defaultTrackProperties();
            const fullPayload = {
                ...defaultTrackProps,
                ...payload,
            };
            if (!this.userAccount || !this.userAccount.email) {
                await this.setAccount();
            }
            analytics.track(trackEvent, fullPayload);
        }
        catch (e) {
            console.error(e);
        }
    }
    async createApplicationTrack({ trackEvent, application, tenant, payload = {} }) {
        if (!trackEvent) {
            return;
        }
        application = application || await this.CurrentApplicationService.fetchApplication();
        let applicationTrackDefaults = {};
        if (application) {
            const channelsActive = application.isSubflow ? [] : await this.getActiveChannels(application);
            applicationTrackDefaults = {
                botName: application.name,
                botIdentifier: application.shortName,
                template: application.template,
                plan: application.plan,
                cluster: application.cluster,
                channelsActive,
            };
        }
        const tenantPayload = await this.getTenantPayload(tenant);
        await this.createTrack(trackEvent, { ...payload, ...tenantPayload, ...applicationTrackDefaults });
    }
    async createOrganizationTrack(trackEvent, payload) {
        const defaultTrackProps = this.defaultTrackProperties();
        if (!trackEvent) {
            return;
        }
        const tenantPayload = await this.getTenantPayload();
        const plan = await this.ContextProcessorService.getCurrentTenantPlan();
        const cluster = await this.ContextProcessorService.getCurrentTenantCluster();
        const fullPayload = {
            plan,
            cluster,
            ...tenantPayload,
            ...defaultTrackProps,
            ...payload,
        };
        this.createTrack(trackEvent, fullPayload);
    }
    async getTenantPayload(tenantInfo = undefined) {
        const tenant = tenantInfo || await this.ContextProcessorService.getCurrentTenant();
        if (tenant.id) {
            return {
                organizationId: tenant.id,
                organizationName: tenant.name,
            };
        }
    }
    async createUpgradeSubscriptionTrack({ trackEvent, shortName, payload = {} }) {
        const application = shortName ?
            await this.CurrentApplicationService.fetchApplication(shortName) : {};
        await this.createApplicationTrack({
            trackEvent,
            application,
            payload,
        });
    }
    /**
     * @deprecated USE createApplicationTrack instead!
     */
    async createBotTrack(trackEvent, application, payload = {}) {
        await this.createApplicationTrack({ trackEvent, application, payload });
    }
    async getActiveChannels(application) {
        let activeChannels = '';
        if (application.shortName !== this.application?.shortName) {
            this.application = application;
            await this.ChannelsService.init();
            this.ChannelsService.setApplication(application);
            this.ChannelsService.setChannelsActivationStatuses();
        }
        const channels = this.ChannelsService.activationStatuses;
        if (channels) {
            activeChannels = Object.keys(channels)
                .filter((channel) => channels[channel] === true)
                .join(';');
        }
        return activeChannels;
    }
    defaultTrackProperties() {
        const properties = {
            screenResolution: this.getScreenResolution(),
            operationalSystem: this.getOperationalSystem(),
            deviceType: this.getDeviceType(),
        };
        return properties;
    }
    getScreenResolution() {
        const resolution = window.screen;
        const width = resolution.width;
        const height = resolution.height;
        return `${width}x${height}`;
    }
    getOperationalSystem() {
        let platform = navigator.platform;
        if (platform.includes('Win32') || platform.includes('WinCE')) {
            platform = 'Windows';
        }
        return platform;
    }
    getDeviceType() {
        const isMobile = () => {
            let check = false;
            ((a) => {
                if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) {
                    check = true;
                }
            })(navigator.userAgent || navigator.vendor);
            return check;
        };
        const isMobileTablet = () => {
            let check = false;
            ((a) => {
                if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) {
                    check = true;
                }
            })(navigator.userAgent || navigator.vendor);
            return check;
        };
        let device = 'Desktop';
        if (isMobile()) {
            device = 'Mobile';
        }
        else if (isMobileTablet()) {
            device = 'Tablet';
        }
        return device;
    }
    page({ name, path }) {
        analytics.page(name, {
            name: name,
            path,
        });
    }
    /**
     * Util function to decide retrieve api account or mix of blip account and api account
     */
    async retrieveUser() {
        const blipAccount = this.BlipService.isConnected() && (await this.AccountService2.me());
        if (blipAccount) {
            return {
                ...this.AuthenticationService.getAccount(),
                extras: { ...blipAccount.extras },
            };
        }
        else {
            return {
                ...this.AuthenticationService.getAccount(),
            };
        }
    }
}
SegmentService.$inject = ['CurrentApplicationService', 'ContextProcessorService', 'AuthenticationService', 'AccountService2', 'BlipService', 'ChannelsService'];
